import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { CalendarClock, Check, ThumbsDown, ThumbsUp } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { getShareLink, respondShareLink } from '@/lib/api'

export const Route = createFileRoute('/s/$token')({ component: SharePage })

// PUBLIC, login-free client surface. The client logs interview feedback per candidate, or proposes
// interview slots. No PII shown (anonymised) until shortlisting.
function SharePage() {
  const { token } = Route.useParams()
  const { data, isLoading } = useQuery({ queryKey: ['share', token], queryFn: () => getShareLink(token), retry: false })
  const [verdicts, setVerdicts] = useState<Record<string, 'advance' | 'reject'>>({})
  const [slots, setSlots] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const submit = async () => {
    setSubmitting(true)
    try {
      const body = data?.kind === 'feedback'
        ? { verdicts: Object.entries(verdicts).map(([handle, decision]) => ({ handle, decision })), note }
        : { slots }
      await respondShareLink(token, body)
      setDone(true)
    } catch {
      toast.error('Could not submit — this link may already have been used.')
    } finally {
      setSubmitting(false)
    }
  }

  const candidates = data?.payload?.candidates ?? []
  const responded = done || data?.status === 'responded'

  return (
    <div className="flex min-h-screen items-start justify-center bg-muted/30 p-4 sm:p-8">
      <div className="w-full max-w-lg">
        <div className="mb-4 text-center">
          <div className="text-lg font-semibold text-foreground">Inumellas Consultancy</div>
          <div className="text-xs text-muted-foreground">Secure candidate review</div>
        </div>

        {isLoading ? (
          <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : !data ? (
          <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">This link is invalid or has expired.</CardContent></Card>
        ) : responded ? (
          <Card><CardContent className="py-12 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-success/15"><Check className="h-5 w-5 text-success" /></div>
            <p className="text-sm font-medium text-foreground">Thank you — your response was recorded.</p>
            <p className="mt-1 text-xs text-muted-foreground">The recruiter has been notified and will follow up.</p>
          </CardContent></Card>
        ) : (
          <Card><CardContent className="pt-5 pb-5">
            <h1 className="flex items-center gap-1.5 text-base font-semibold text-foreground">
              {data.kind === 'feedback' ? <><ThumbsUp className="h-4 w-4 text-primary" /> Interview feedback</> : <><CalendarClock className="h-4 w-4 text-primary" /> Propose interview times</>}
            </h1>
            <p className="text-sm text-muted-foreground">{data.title}{data.company ? ` · ${data.company}` : ''}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Profiles are anonymised; full contact details follow on shortlisting.</p>

            <div className="mt-3 space-y-2">
              {candidates.map((c) => (
                <div key={c.handle} className="rounded-md border border-border p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{c.handle}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">fit {c.fit}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{c.headline}</div>
                  {c.skills.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{c.skills.map((s) => <span key={s} className="rounded bg-muted/50 px-1 py-0.5 text-[11px] text-muted-foreground">{s}</span>)}</div>}
                  {data.kind === 'feedback' && (
                    <div className="mt-2 flex gap-1.5">
                      <button onClick={() => setVerdicts((v) => ({ ...v, [c.handle]: 'advance' }))} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${verdicts[c.handle] === 'advance' ? 'border-success bg-success/10 text-success' : 'border-input text-muted-foreground hover:bg-muted/50'}`}><ThumbsUp className="h-3.5 w-3.5" /> Advance</button>
                      <button onClick={() => setVerdicts((v) => ({ ...v, [c.handle]: 'reject' }))} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${verdicts[c.handle] === 'reject' ? 'border-destructive bg-destructive/10 text-destructive' : 'border-input text-muted-foreground hover:bg-muted/50'}`}><ThumbsDown className="h-3.5 w-3.5" /> Pass</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {data.kind === 'feedback' ? (
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note for the recruiter…" className="mt-3 w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            ) : (
              <textarea value={slots} onChange={(e) => setSlots(e.target.value)} rows={3} placeholder="Suggest 2–3 interview slots (e.g. Tue 3–5pm, Wed 11am)…" className="mt-3 w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            )}

            <Button className="mt-3 w-full" disabled={submitting} onClick={submit}>{submitting ? 'Submitting…' : 'Submit'}</Button>
          </CardContent></Card>
        )}
      </div>
    </div>
  )
}
